import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { apiClient } from '../../../utils/apiClient';
import TopBar from '../../../layout/TopBar';
import ProfilePictureManager from '../../../layout/ProfilePictureManager';
import CheckBadge from '../../../macadamia-core/icons/CheckBadge';
import DeleteAccountModal from '../components/DeleteAccountModal';
import InterestsPanel from '../components/InterestsPanel';
import WebAuthnDevicesPanel from '../components/WebAuthnDevicesPanel';

const Profile = () => {
    const { user, setUser, logout } = useAuth();
    const navigate = useNavigate();
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const [profileData, setProfileData] = useState({
        linkedinUrl: '',
        websiteUrl: '',
        publicEmail: '',
        bio: '',
        category: 'AVOCAT',
        subcategory: '',
        publicProfile: false
    });

    const [isLoading, setIsLoading] = useState(false);
    const [saveMessage, setSaveMessage] = useState('');
    const [emailError, setEmailError] = useState('');
    const [identityVerified, setIdentityVerified] = useState(false);

    const maxBioLength = 160;

    // Nouvelle structure de catégories avec keys et labels
    const CATEGORIES_DATA = [
        {
            key: 'AVOCAT',
            label: 'Avocat',
            subcategories: [
                { key: 'BARREAU', label: 'Avocat au barreau' },
                { key: 'AFFAIRES', label: "Avocat d'affaires" },
                { key: 'CONSEIL', label: "Avocat conseil" },
                { key: 'ENTREPRISE', label: "Avocat en entreprise" },
            ],
        },
        {
            key: 'MAGISTRATURE',
            label: 'Magistrature',
            subcategories: [
                { key: 'JUGE', label: 'Juge' },
                { key: 'PROCUREUR', label: 'Procureur' },
                { key: 'GREFFIER', label: 'Greffier' },
                { key: 'AUTRE', label: 'Autre magistrat' },
            ],
        },
        {
            key: 'JURISTE',
            label: 'Juriste',
            subcategories: [
                { key: 'JUNIOR_SENIOR', label: 'Juriste junior / senior' },
                { key: 'DIRECTEUR_JURIDIQUE', label: 'Directeur juridique' },
                { key: 'SECTEUR_PUBLIC', label: 'Juriste secteur public' },
                { key: 'COMPLIANCE', label: 'Compliance officer' },
                { key: 'NOTAIRE_HUISSIER', label: 'Notaire / Huissier' },
            ],
        },
        {
            key: 'UNIVERSITAIRE',
            label: 'Universitaire',
            subcategories: [
                { key: 'ENSEIGNANT_CHERCHEUR', label: 'Enseignant-chercheur' },
                { key: 'CHERCHEUR', label: 'Chercheur' },
                { key: 'DOCTORANT', label: 'Doctorant' },
                { key: 'ENSEIGNANT', label: 'Enseignant' },
            ],
        },
        {
            key: 'ETUDIANT',
            label: 'Étudiant',
            subcategories: [
                { key: 'LICENCE', label: 'Licence droit' },
                { key: 'MASTER', label: 'Master droit' },
                { key: 'ECOLE_AVOCAT', label: "École d'avocat (CRFPA)" },
                { key: 'AUTRES_FORMATIONS', label: 'Autres formations juridiques' },
            ],
        },
        {
            key: 'ENTREPRISE',
            label: 'Entreprise / Organisation',
            subcategories: [
                { key: 'DIRECTION', label: 'Direction / Management' },
                { key: 'INDEPENDANT', label: 'Indépendant / Freelance' },
                { key: 'EMPLOYE', label: 'Employé' },
                { key: 'AUTRE_ENT', label: 'Autre' },
            ],
        },
    ];

    // Fonctions de nettoyage des données
    const cleanLinkedInUrl = (url: string): string => {
        if (!url.trim()) return '';

        // Supprimer les espaces
        const cleaned = url.trim();

        // Extraire l'identifiant LinkedIn depuis différents formats
        const patterns = [
            /(?:https?:\/\/)?(?:www\.)?linkedin\.com\/in\/([^/?\s]+)/i,
            /^\/in\/([^/?\s]+)/i,
            /^([a-zA-Z0-9-]+)$/  // Si c'est déjà juste l'identifiant
        ];

        for (const pattern of patterns) {
            const match = cleaned.match(pattern);
            if (match && match[1]) {
                return match[1];
            }
        }

        // Si aucun pattern ne correspond, vérifier si c'est un identifiant valide
        if (/^[a-zA-Z0-9-]+$/.test(cleaned)) {
            return cleaned;
        }

        return url; // Retourner l'original si on n'arrive pas à l'analyser
    };

    const cleanWebsiteUrl = (url: string): string => {
        if (!url.trim()) return '';

        let cleaned = url.trim();

        // Supprimer le protocole
        cleaned = cleaned.replace(/^https?:\/\//, '');

        // Supprimer le slash final et tout ce qui suit
        cleaned = cleaned.split('/')[0];

        // Supprimer les paramètres et fragments
        cleaned = cleaned.split('?')[0].split('#')[0];

        return cleaned;
    };

    const cleanEmail = (email: string): string => {
        const cleaned = email.trim().toLowerCase();

        // Validation email basique
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(cleaned)) {
            return email; // Retourner l'original pour que l'utilisateur voie l'erreur
        }

        return cleaned;
    };

    const validateEmail = (email: string): boolean => {
        if (!email) return true; // Email vide est valide (optionnel)
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email.trim());
    };

    // Fonction pour trouver une catégorie par sa clé
    const findCategoryByKey = (key: string) => {
        return CATEGORIES_DATA.find(category => category.key === key);
    };

    // Fonction pour trouver les sous-catégories d'une catégorie
    const getSubcategoriesForCategory = (categoryKey: string) => {
        const category = findCategoryByKey(categoryKey);
        return category ? category.subcategories : [];
    };

    // Charger les données utilisateur au montage du composant
    useEffect(() => {
        const loadUserProfile = async () => {

            if (user) {
                try {
                    // Récupérer les données complètes via l'API
                    const response = await apiClient('/users/me');

                    if (response.ok) {
                        const fullUserData = await response.json();

                        setProfileData({
                            linkedinUrl: fullUserData.linkedinUrl || '',
                            websiteUrl: fullUserData.websiteUrl || '',
                            publicEmail: fullUserData.publicEmail || '',
                            bio: fullUserData.bio || '',
                            category: fullUserData.category || 'AVOCAT',
                            subcategory: fullUserData.subcategory || '',
                            publicProfile: fullUserData.publicProfile || false
                        });
                        setIdentityVerified(fullUserData.identityVerified || false);
                    } else {
                        console.error('Erreur lors de la récupération des données utilisateur');
                        // Fallback sur les données du contexte
                        setProfileData({
                            linkedinUrl: user.linkedinUrl || '',
                            websiteUrl: user.websiteUrl || '',
                            publicEmail: user.publicEmail || '',
                            bio: user.bio || '',
                            category: user.category || 'AVOCAT',
                            subcategory: user.subcategory || '',
                            publicProfile: user.publicProfile || false
                        });
                    }
                } catch (error) {
                    console.error('Erreur lors de l\'appel API:', error);
                    // Fallback sur les données du contexte
                    setProfileData({
                        linkedinUrl: user.linkedinUrl || '',
                        websiteUrl: user.websiteUrl || '',
                        publicEmail: user.publicEmail || '',
                        bio: user.bio || '',
                        category: user.category || 'AVOCAT',
                        subcategory: user.subcategory || '',
                        publicProfile: user.publicProfile || false
                    });
                }
            } else {
                // Valeurs par défaut même sans utilisateur connecté
                setProfileData({
                    linkedinUrl: '',
                    websiteUrl: '',
                    publicEmail: '',
                    bio: '',
                    category: 'AVOCAT',
                    subcategory: '',
                    publicProfile: false
                });
            }
        };

        loadUserProfile();
    }, [user?.id]);

    const handleInputChange = (field: string, value: string) => {
        // Cas spécial pour le toggle boolean
        if (field === 'publicProfile') {
            setProfileData(prev => ({ ...prev, publicProfile: !prev.publicProfile }));
            return;
        }

        setProfileData(prev => {
            let cleanedValue = value;

            // Appliquer le nettoyage automatique selon le champ
            switch (field) {
                case 'linkedinUrl':
                    cleanedValue = cleanLinkedInUrl(value);
                    break;
                case 'websiteUrl':
                    cleanedValue = cleanWebsiteUrl(value);
                    break;
                case 'publicEmail':
                    cleanedValue = cleanEmail(value);
                    // Validation de l'email
                    if (cleanedValue && !validateEmail(cleanedValue)) {
                        setEmailError('Format d\'email invalide');
                    } else {
                        setEmailError('');
                    }
                    break;
                default:
                    cleanedValue = value;
            }

            const updated = {
                ...prev,
                [field]: cleanedValue
            };

            // Si on change de catégorie, réinitialiser la sous-catégorie
            if (field === 'category') {
                updated.subcategory = '';
            }

            return updated;
        });
    };

    const handleSave = async () => {

        // Validation avant sauvegarde
        if (profileData.publicEmail && !validateEmail(profileData.publicEmail)) {
            setSaveMessage('Veuillez corriger l\'email avant de sauvegarder');
            return;
        }

        setIsLoading(true);
        setSaveMessage('');

        try {

            const response = await apiClient('/users/profile', {
                method: 'PATCH',
                body: JSON.stringify(profileData)
            });


            const responseText = await response.text();

            if (response.ok) {
                try {
                    const responseData = JSON.parse(responseText);

                    // Mettre à jour le contexte Auth avec les nouvelles données
                    if (responseData.user && user) {
                        const updatedUser = {
                            ...user,
                            bio: responseData.user.bio,
                            websiteUrl: responseData.user.websiteUrl,
                            linkedinUrl: responseData.user.linkedinUrl,
                            publicEmail: responseData.user.publicEmail,
                            publicProfile: responseData.user.publicProfile,
                            category: responseData.user.category,
                            subcategory: responseData.user.subcategory
                        };
                        setUser(updatedUser);
                        localStorage.setItem('user', JSON.stringify(updatedUser));
                    }

                    setSaveMessage('Profil mis à jour avec succès !');
                    setTimeout(() => setSaveMessage(''), 3000);
                } catch (parseError) {
                    console.error('Erreur de parsing JSON (mais statut OK):', parseError);
                    setSaveMessage('Profil mis à jour mais erreur de réponse');
                }
            } else {
                console.error('Erreur HTTP:', response.status);
                try {
                    const errorData = JSON.parse(responseText);
                    setSaveMessage(errorData.message || 'Erreur lors de la sauvegarde');
                } catch (parseError) {
                    console.error('Impossible de parser l\'erreur:', parseError);
                    setSaveMessage(`Erreur ${response.status}: ${responseText.substring(0, 100)}...`);
                }
            }
        } catch (error) {
            console.error('Erreur lors de la sauvegarde:', error);
            setSaveMessage('Erreur lors de la sauvegarde');
        } finally {
            setIsLoading(false);
        }
    };

    const socialPlatforms = [
        {
            name: 'Site Web',
            field: 'websiteUrl',
            baseUrl: '',
            placeholder: 'mondomaine.com',
            helpText: 'Ex: mondomaine.com ou www.mondomaine.fr',
            icon: (
                <div className="w-5 h-5 rounded-lg bg-mcdm-secondary-400 flex items-center justify-center">
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.94-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
                    </svg>
                </div>
            )
        },
        {
            name: 'LinkedIn',
            field: 'linkedinUrl',
            baseUrl: 'linkedin.com/in/',
            placeholder: 'votre-profil',
            helpText: 'Ex: votre-profil ou linkedin.com/in/votre-profil',
            icon: (
                <div className="w-5 h-5 rounded-lg bg-mcdm-secondary-400 flex items-center justify-center">
                    <svg className="w-3 h-3 text-white" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                    </svg>
                </div>
            )
        },
        {
            name: 'Email Public',
            field: 'publicEmail',
            baseUrl: '',
            placeholder: 'votre@email.fr',
            helpText: 'Email de contact public',
            icon: (
                <div className="w-5 h-5 rounded-lg bg-mcdm-secondary-400 flex items-center justify-center">
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                    </svg>
                </div>
            )
        }
    ];

    return (
        <div className="min-h-screen bg-mcdm-primary-200 dark:bg-mcdm-third-500">
            <TopBar />
            <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
                <div className="max-w-3xl mx-auto bg-white dark:bg-mcdm-third-400 p-8 rounded-lg shadow-sm dark:shadow-inner">
                    <h1 className="text-xl font-platypi text-mcdm-third-500 dark:text-mcdm-primary-100 mb-6">Mon Profil</h1>

                    <div className="flex items-center space-x-4 mb-8">
                        <div className="relative">
                            <ProfilePictureManager
                                size="lg"
                                editable
                                className="rounded-full shadow-sm dark:shadow-inner hover:shadow-md dark:hover:shadow-md transition-all duration-200"
                            />
                        </div>
                        <div>
                            <div className="flex items-center gap-1.5">
                                <p className="text-lg font-medium text-gray-900 dark:text-mcdm-primary-200">{user?.name}</p>
                                {identityVerified && (
                                    <CheckBadge width="20" height="20" fill="var(--green-400)" className="flex-shrink-0" style={{ marginTop: '3px' }} />
                                )}
                            </div>
                            <p className="text-sm text-gray-500 dark:text-mcdm-primary-100">{user?.email}</p>
                        </div>
                    </div>

                    {/* Toggle profil public */}
                    <div className="mb-6 p-4 bg-gray-50 dark:bg-mcdm-third-500 border border-gray-200 dark:border-mcdm-third-300 rounded-lg">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-sm font-semibold text-gray-900 dark:text-mcdm-primary-200">
                                    Activer mon profil public
                                </h3>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    Les autres utilisateurs pourront voir votre profil et vos publications publiques
                                </p>
                                {profileData.publicProfile && user?.id && (
                                    <Link
                                        to={`/profil/${user.id}`}
                                        className="inline-flex items-center text-xs text-mcdm-green-400 hover:text-mcdm-green-300 mt-2 transition-colors"
                                    >
                                        Voir mon profil public →
                                    </Link>
                                )}
                            </div>
                            <button
                                onClick={() => handleInputChange('publicProfile', (!profileData.publicProfile).toString())}
                                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
                                    profileData.publicProfile
                                        ? 'bg-mcdm-green-400'
                                        : 'bg-gray-300 dark:bg-mcdm-third-300'
                                }`}
                                role="switch"
                                aria-checked={profileData.publicProfile}
                            >
                                <span
                                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                        profileData.publicProfile ? 'translate-x-5' : 'translate-x-0'
                                    }`}
                                />
                            </button>
                        </div>
                    </div>

                    <div className="mt-8">
                        {/* Catégories professionnelles */}
                        <div className="mb-6">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-mcdm-primary-200 mb-3">
                                Votre activité
                            </h3>
                            <div className="flex flex-wrap gap-2 mb-4">
                                {CATEGORIES_DATA.map((category) => (
                                    <button
                                        key={category.key}
                                        onClick={() => handleInputChange('category', category.key)}
                                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${profileData.category === category.key
                                            ? 'bg-mcdm-green-400 text-white'
                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-mcdm-third-300 dark:text-mcdm-primary-200 dark:hover:bg-mcdm-third-200'
                                            }`}
                                    >
                                        {category.label}
                                    </button>
                                ))}
                            </div>

                            {/* Sous-catégories */}
                            {profileData.category && getSubcategoriesForCategory(profileData.category).length > 0 && (
                                <div>
                                    <h4 className="text-sm font-medium text-gray-700 dark:text-mcdm-primary-200 mb-2">
                                        Profession exercée
                                    </h4>
                                    <div className="flex flex-wrap gap-2">
                                        {getSubcategoriesForCategory(profileData.category).map((subcategory) => (
                                            <button
                                                key={subcategory.key}
                                                onClick={() => handleInputChange('subcategory', subcategory.key)}
                                                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors duration-200 ${profileData.subcategory === subcategory.key
                                                    ? 'bg-mcdm-third-500 dark:bg-mcdm-primary-100 text-white dark:text-mcdm-third-500'
                                                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100 dark:bg-mcdm-third-400 dark:text-mcdm-primary-200 dark:hover:bg-mcdm-third-300'
                                                    }`}
                                            >
                                                {subcategory.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <h2 className="text-lg font-semibold text-gray-900 dark:text-mcdm-primary-200 mb-4">
                            À propos de vous
                        </h2>

                        <div className="relative mb-8 bg-white dark:bg-mcdm-third-500 border border-gray-200 dark:border-mcdm-third-300 rounded-lg shadow-xs">
                            <div className="absolute top-3 right-3 text-xs text-gray-400 dark:text-mcdm-primary-100">
                                {profileData.bio.length}/{maxBioLength}
                            </div>
                            <textarea
                                placeholder="Parlez-nous de vous"
                                value={profileData.bio}
                                onChange={(e) => {
                                    if (e.target.value.length <= maxBioLength) {
                                        handleInputChange('bio', e.target.value);
                                    }
                                }}
                                className="w-full p-4 pr-16 bg-transparent text-mcdm-third-500 dark:text-mcdm-primary-100 placeholder-gray-400 dark:placeholder-mcdm-primary-100 outline-none focus:ring-2 focus:ring-mcdm-primary-500 rounded-lg resize-none"
                                style={{ height: '83px' }}
                            />
                        </div>

                        <h2 className="text-lg font-semibold text-gray-900 dark:text-mcdm-primary-200 mb-4">
                            Informations supplémentaires
                        </h2>

                        <div className="space-y-3">
                            {socialPlatforms.map((platform) => (
                                <div key={platform.field}>
                                    <div className="flex items-center bg-white dark:bg-mcdm-third-500 border border-gray-200 dark:border-mcdm-third-300 rounded-lg mb-1 shadow-xs overflow-hidden">
                                        <div className="flex items-center bg-gray-50 dark:bg-mcdm-third-400 px-4 py-3">
                                            <div className="flex-shrink-0 mr-3">
                                                {platform.icon}
                                            </div>
                                            <span className="text-gray-600 dark:text-mcdm-primary-200 text-sm">{platform.baseUrl || platform.name}</span>
                                        </div>

                                        <div className="flex-grow flex items-center px-4 py-3">
                                            <input
                                                type={platform.field === 'publicEmail' ? 'email' : 'text'}
                                                placeholder={platform.placeholder}
                                                value={profileData[platform.field as keyof typeof profileData] as string}
                                                onChange={(e) => handleInputChange(platform.field, e.target.value)}
                                                className="flex-grow outline-none text-mcdm-third-500 dark:text-mcdm-primary-100 placeholder-gray-400 dark:placeholder-mcdm-primary-500 text-sm bg-transparent"
                                            />
                                        </div>

                                        {profileData[platform.field as keyof typeof profileData] && (
                                            <div className="flex-shrink-0 px-4">
                                                <div className="w-4 h-4 bg-mcdm-green-300 rounded-full flex items-center justify-center">
                                                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                    </svg>
                                                </div>
                                            </div>
                                        )}
                                    </div>


                                    {/* Erreur email */}
                                    {platform.field === 'publicEmail' && emailError && (
                                        <p className="text-xs text-red-600 dark:text-red-400 mb-2 px-1">
                                            {emailError}
                                        </p>
                                    )}
                                </div>
                            ))}
                        </div>

                        {/* Message de feedback */}
                        {saveMessage && (
                            <div className={`mt-4 p-3 rounded-lg text-sm ${saveMessage.includes('succès')
                                ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100'
                                : 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100'
                                }`}>
                                {saveMessage}
                            </div>
                        )}

                        <div className="mt-8 flex justify-start">
                            <button
                                onClick={handleSave}
                                disabled={isLoading}
                                className={`px-6 py-2 ${isLoading
                                    ? 'bg-mcdm-green-300 cursor-not-allowed'
                                    : 'bg-mcdm-green-400 hover:bg-mcdm-green-300'
                                    } text-white rounded-lg transition-colors duration-200 text-sm`}
                            >
                                {isLoading ? 'Enregistrement...' : 'Enregistrer'}
                            </button>
                        </div>

                        <div className="mt-12 pt-6 border-t border-gray-200 dark:border-mcdm-third-300">
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-mcdm-primary-200 mb-4">
                                Centres d'intérêt
                            </h2>
                            <div className="border border-gray-200 dark:border-mcdm-third-300 rounded-lg p-4 mb-8">
                                <InterestsPanel
                                    currentInterests={user?.onboardingInterests ?? []}
                                    onSaved={(interests) =>
                                        setUser((prev) =>
                                            prev ? { ...prev, onboardingInterests: interests, hasOnboardingInterests: interests.length > 0 } : prev
                                        )
                                    }
                                />
                            </div>

                            <h2 className="text-lg font-semibold text-gray-900 dark:text-mcdm-primary-200 mb-4">
                                Sécurité
                            </h2>
                            <div className="border border-gray-200 dark:border-mcdm-third-300 rounded-lg p-4 mb-8">
                                <WebAuthnDevicesPanel />
                            </div>

                            <h2 className="text-lg font-semibold text-red-600 dark:text-red-400 mb-4">
                                Zone de danger
                            </h2>
                            <div className="border border-red-200 dark:border-red-800 rounded-lg p-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-mcdm-third-500 dark:text-mcdm-primary-100">
                                            Supprimer mon compte
                                        </p>
                                        <p className="text-xs text-mcdm-third-400 dark:text-mcdm-primary-300 mt-1">
                                            Cette action est irréversible. Vos données personnelles seront anonymisées après 30 jours.
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => setShowDeleteModal(true)}
                                        className="ml-4 px-4 py-2 text-sm rounded-lg border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors whitespace-nowrap"
                                    >
                                        Supprimer mon compte
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <DeleteAccountModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={async (password, reason) => {
                    const response = await apiClient('/users/delete-account', {
                        method: 'POST',
                        body: JSON.stringify({ password, reason }),
                    });

                    if (!response.ok) {
                        const data = await response.json();
                        throw new Error(data.message || 'Erreur lors de la suppression');
                    }

                    logout();
                    navigate('/login', { replace: true });
                }}
            />
        </div>
    );
};

export default Profile;